/*
 * Copyright 2026 Sony Corporation
 */

import { MAX_DIGEST_NODES, type DigestNode, type SceneDigest } from './schema';
import type { SceneDoc, SceneNode, Vec3 } from './schema';

/**
 * What the agent is told about the scene (devplan §7.3).
 *
 * Ids, names, kinds, rounded positions and sizes — and nothing else. Not the
 * document, because a document carries material references and pinned hashes
 * that cost tokens and decide nothing; never a URL, a token or raw `.babylon`
 * bytes.
 *
 * Pure, so the digest of a document is a value a test can assert on, which is
 * what makes an eval suite possible without a renderer or a model.
 */
export { MAX_DIGEST_NODES };
export type { DigestNode, SceneDigest };

const round = (value: number): number => Math.round(value * 100) / 100;

const roundVec = (vec: Vec3): Vec3 => [round(vec[0]), round(vec[1]), round(vec[2])];

const scaleOf = (node: SceneNode): number => Math.max(...node.transform.scale.map(Math.abs));

const sizeOf = (node: SceneNode): number | undefined =>
  node.kind === 'primitive' ? round(node.primitive.size * scaleOf(node)) : undefined;

/**
 * The scene's extent, from geometry only: a light sitting six metres up is not
 * something the scene is built around, and counting it pushes every distance
 * derived from the radius outwards.
 */
const boundsOf = (nodes: readonly SceneNode[]): { center: Vec3; radius: number } => {
  const geometry = nodes.filter((node) => node.kind !== 'light');
  if (geometry.length === 0) {
    return { center: [0, 0, 0], radius: 0 };
  }

  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const node of geometry) {
    // An asset's own dimensions are not in the document, so a metre stands in
    // for one: an estimate that is honest at this precision and wrong by less
    // than the rounding on everything else.
    const half = (node.kind === 'primitive' ? node.primitive.size * scaleOf(node) : scaleOf(node)) / 2;
    for (let axis = 0; axis < 3; axis += 1) {
      min[axis] = Math.min(min[axis], node.transform.position[axis] - half);
      max[axis] = Math.max(max[axis], node.transform.position[axis] + half);
    }
  }

  const span: Vec3 = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
  return {
    center: roundVec([min[0] + span[0] / 2, min[1] + span[1] / 2, min[2] + span[2] / 2]),
    radius: round(Math.hypot(span[0], span[1], span[2]) / 2),
  };
};

const typeOf = (node: SceneNode): string => {
  switch (node.kind) {
    case 'primitive':
      return node.primitive.type;
    case 'light':
      return node.light.type;
    case 'asset':
      return node.asset.slug;
  }
};

export const digestScene = (doc: SceneDoc, selection: readonly string[] = []): SceneDigest => {
  const kept = doc.nodes.slice(0, MAX_DIGEST_NODES);

  return {
    units: doc.units,
    bounds: boundsOf(doc.nodes),
    ...(doc.environment ? { environment: doc.environment.hdri.slug } : {}),
    selection: [...selection],
    nodes: kept.map((node) => {
      const size = sizeOf(node);
      return {
        id: node.id,
        name: node.name,
        kind: node.kind,
        type: typeOf(node),
        position: roundVec(node.transform.position),
        ...(size === undefined ? {} : { size }),
      };
    }),
    ...(doc.nodes.length > kept.length ? { omitted: doc.nodes.length - kept.length } : {}),
  };
};
